import { createSelector } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQuery } from "../../../shared/api/base-query";
import { filterNotDeleted } from "../../../shared/api/filter-not-deleted";
import { pagedQueryFn } from "../../../shared/api/paged-query-fn";
import type { QuerySubscriptionOptions } from "../../../shared/api/query-subscription-options";
import type { IChemicalsDto } from "../shared";
import { Chemical } from "../shared/models/chemical";

export const chemicalsApiSlice = createApi({
  reducerPath: "api-chemicals",
  // https://github.com/reduxjs/redux-toolkit/discussions/2347#discussioncomment-2978038
  refetchOnFocus: false,
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  baseQuery,
  tagTypes: ["ChemicalsList", "ChemicalsListCount"],
  endpoints: (builder) => ({
    fetchChemicalsWithDeleted: builder.query<Chemical[], void>({
      queryFn: pagedQueryFn<IChemicalsDto, Chemical[]>(
        { url: "ChemicalsV2", params: { includeDeleted: true } },
        (dtos) => {
          const models: Chemical[] = dtos.map((dto) => {
            const model = new Chemical(dto.id);
            model.updateFromDto(dto);
            return model;
          });
          models.sort((a: Chemical, b: Chemical) => a.name.localeCompare(b.name));
          return models;
        }
      ),
    }),
  }),
});

const selectNotDeletedData = createSelector(
  ({ data }: { data?: Chemical[] }) => data,
  filterNotDeleted<Chemical>
);

const selectNotDeletedCurrentData = createSelector(
  ({ currentData }: { currentData?: Chemical[] }) => currentData,
  filterNotDeleted<Chemical>
);

const useChemicalsEndpoint =
  chemicalsApiSlice.endpoints.fetchChemicalsWithDeleted.useQuery;

type ChemicalsQueryOptions = QuerySubscriptionOptions<typeof useChemicalsEndpoint>;

export function useFetchChemicals(options?: ChemicalsQueryOptions) {
  return useChemicalsEndpoint(undefined, {
    ...options,
    selectFromResult: (result) => ({
      ...result,
      data: selectNotDeletedData(result),
      currentData: selectNotDeletedCurrentData(result),
    }),
  });
}

export function useFetchChemicalsWithDeleted(options?: ChemicalsQueryOptions) {
  return useChemicalsEndpoint(undefined, options);
}
